"use client";

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Eye } from 'lucide-react';
import { format } from 'date-fns';
import type { AuditLog } from '@/hooks/audit';
import { Table, type ITableColumn, type IRowWithDisplay } from '@/components/table/table';

interface AuditLogTableProps {
  data: AuditLog[];
  isLoading: boolean;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    onPageChange: (page: number) => void;
  };
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  externalSearchValue?: string;
  onExternalSearchChange?: (value: string) => void;
}

export const AuditLogTable: React.FC<AuditLogTableProps> = ({
  data,
  isLoading,
  pagination,
  sortBy,
  sortOrder,
  onSortChange,
  externalSearchValue,
  onExternalSearchChange,
}) => {
  const { t } = useTranslation();
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const getStatusPill = (status: string) => {
    const isSuccess = status === 'SUCCESS';
    const colorClasses = isSuccess
      ? 'bg-green-100 text-green-700 border border-green-200'
      : 'bg-red-100 text-red-700 border border-red-200';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClasses}`}>
        {isSuccess ? t('auditLogs.success') : t('auditLogs.failure')}
      </span>
    );
  };

  const formatAction = (action: string) => {
    return action
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatTimestamp = (timestamp: string) => {
    return format(new Date(timestamp), 'MMM dd, yyyy HH:mm');
  };

  const getUserPrimary = (log: AuditLog) => {
    if (!log.user) return t('auditLogs.system');
    const { firstName, lastName, email } = log.user as any;
    return firstName && lastName ? `${firstName} ${lastName}` : (email || t('auditLogs.system'));
  };
  const getUserEmail = (log: AuditLog) => {
    return log.user?.email || '';
  };

  const columns: ITableColumn[] = useMemo(() => [
    { key: 'timestamp', label: t('auditLogs.timestamp') },
    { key: 'user', label: t('auditLogs.user') },
    { key: 'action', label: t('auditLogs.action') },
    { key: 'resource', label: t('auditLogs.resource') },
    { key: 'status', label: t('auditLogs.status') },
    { key: 'actions', label: t('common.actions'), width: 80, resizable: false },
  ], [t]);

  const tableData: IRowWithDisplay[] = useMemo(() => {
    return data.map((log) => {
      const row: any = {
        timestamp: formatTimestamp(log.timestamp),
        user: getUserPrimary(log),
        action: formatAction(log.action),
        resource: log.resource || '-',
        status: log.status,
        details: log.details ? t('common.available') : '-',
        actions: '',
      };

      row._display = {
        user: (
          <div className="max-w-[220px]" title={getUserPrimary(log)}>
            <div className="truncate">{getUserPrimary(log)}</div>
            {getUserEmail(log) && (
              <div className="text-xs text-muted-foreground truncate">{getUserEmail(log)}</div>
            )}
          </div>
        ),
        action: (
          <div className="max-w-[200px] truncate" title={formatAction(log.action)}>
            {formatAction(log.action)}
          </div>
        ),
        resource: (
          <div className="max-w-[160px] truncate" title={log.resource || '-'}>
            {log.resource || '-'}
          </div>
        ),
        status: getStatusPill(log.status),
        actions: (
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedLog(log)}
              >
                <Eye className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{t('auditLogs.logDetails')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('auditLogs.timestamp')}
                    </label>
                    <p className="text-sm">{formatTimestamp(log.timestamp)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('auditLogs.user')}
                    </label>
                    <div className="text-sm">
                      <div>{getUserPrimary(log)}</div>
                      {getUserEmail(log) && (
                        <div className="text-xs text-muted-foreground">{getUserEmail(log)}</div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('auditLogs.action')}
                    </label>
                    <p className="text-sm">{formatAction(log.action)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('auditLogs.status')}
                    </label>
                    <div className="mt-1">{getStatusPill(log.status)}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('auditLogs.resource')}
                    </label>
                    <p className="text-sm">{log.resource || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('auditLogs.resourceId')}
                    </label>
                    <p className="text-xs font-mono">{log.resourceId || '-'}</p>
                  </div>
                </div>
                {log.details && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('auditLogs.details')}
                    </label>
                    <div className="h-32 w-full border rounded-md p-2 mt-1 overflow-auto text-xs text-muted-foreground">
                      <pre>{JSON.stringify(log.details, null, 2)}</pre>
                    </div>
                  </div>
                )}
                {log.errorMessage && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('auditLogs.errorMessage')}
                    </label>
                    <p className="text-sm text-red-700 bg-red-100 border border-red-200 p-2 rounded-md mt-1">
                      {log.errorMessage}
                    </p>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        ),
      };

      return row as IRowWithDisplay;
    });
  }, [data, t]);

  // Use shared table's empty and loading states

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('auditLogs.auditLogs')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="p-4">
            <Table
              title={t('auditLogs.auditLogs')}
              columns={columns}
              data={tableData}
              isLoading={isLoading}
              paginate
              pagination={{
                page: pagination.currentPage,
                limit: 20,
                total: pagination.totalItems,
                pages: pagination.totalPages,
              }}
              onPageChange={pagination.onPageChange}
              showSearch
              externalSearchValue={externalSearchValue}
              onExternalSearchChange={onExternalSearchChange}
              sortBy={sortBy || undefined}
              sortOrder={sortOrder || undefined}
              onSortChange={onSortChange}
            />
          </div>
        </CardContent>
      </Card>
    </>
  );
};
